import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const SpaceModal = ({ space, onClose, onActionComplete }) => {
    const { user } = useContext(AuthContext);
    const [vehicleId, setVehicleId] = useState('');
    const [vehicles, setVehicles] = useState([]);
    const [vehicleSearch, setVehicleSearch] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (space && space.status === 'GREEN') {
            const fetchVehicles = async () => {
                try {
                    const response = await axios.get('/vehicles');
                    const filteredVehicles = response.data.filter(v => v.organization_id === space.organization_id);
                    setVehicles(filteredVehicles);
                } catch (err) {
                    setError("Could not load the vehicle list.");
                }
            };
            fetchVehicles();
        }
    }, [space]);

    if (!space) return null;

    const handleAssign = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            await axios.post(`/spaces/${space.id}/assign`, { vehicle_id: parseInt(vehicleId) });
            onActionComplete(); onClose(); 
        } catch (err) { setError(err.response?.data?.detail || "Failed to assign vehicle."); } 
        finally { setLoading(false); }
    };

    const handleRelease = async () => {
        setError(''); setLoading(true);
        try {
            await axios.post(`/spaces/${space.id}/release`);
            onActionComplete(); onClose(); 
        } catch (err) { setError(err.response?.data?.detail || "Failed to release vehicle."); } 
        finally { setLoading(false); }
    };

    const handleOverride = async (targetStatus) => {
        setError(''); setLoading(true);
        try {
            await axios.patch(`/spaces/${space.id}/override`, { status: targetStatus });
            onActionComplete(); onClose(); 
        } catch (err) { setError(err.response?.data?.detail || "Failed to override space status."); } 
        finally { setLoading(false); }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div className="glass-card" style={{ padding: '30px', width: '400px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0 }}>Space {space.space_number}</h2>
                    <button onClick={onClose} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: '18px', color: 'var(--text-color)' }}>✖</button>
                </div>

                {error && <div style={{ color: 'black', marginBottom: '15px', padding: '10px', backgroundColor: 'var(--danger-hover)', borderRadius: '4px' }}>{error}</div>}

                <div style={{ marginBottom: '20px' }}>
                    <strong>Status: </strong> 
                    <span style={{ fontWeight: 'bold', color: space.status === 'GREEN' ? 'var(--success)' : (space.status === 'RED' ? 'var(--danger)' : (space.status === 'OUT_OF_SERVICE' ? 'var(--out-of-service)' : 'var(--warning)')) }}>
                        {space.status.replace(/_/g, ' ')}
                    </span>
                </div>

                {/* Conditional Admin Override Controls */}
                {user?.role === 'admin' && (space.status === 'GREEN' || space.status === 'OUT_OF_SERVICE') && (
                    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--warning-bg)', borderRadius: '4px', border: '1px solid var(--warning)' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--warning)' }}>Admin Controls</h4>
                        {space.status === 'GREEN' ? (
                            <button onClick={() => handleOverride('OUT_OF_SERVICE')} disabled={loading} className="btn" style={{ width: '100%', backgroundColor: 'var(--out-of-service)' }}>
                                {loading ? 'Processing...' : 'Mark as Out of Service'}
                            </button>
                        ) : (
                            <button onClick={() => handleOverride('GREEN')} disabled={loading} className="btn" style={{ width: '100%', backgroundColor: 'var(--success)' }}>
                                {loading ? 'Processing...' : 'Restore to Service'}
                            </button>
                        )}
                    </div>
                )}

                {/* Standard Assign/Release UI */}
                {space.status === 'GREEN' && (
                    <form onSubmit={handleAssign}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Select Vehicle to Assign:</label>
                            <input 
                                type="text" 
                                placeholder="Search by truck number..." 
                                value={vehicleSearch}
                                onChange={(e) => setVehicleSearch(e.target.value)}
                                style={{ width: '100%', marginBottom: '10px', padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                            />
                            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required size="5" style={{ width: '100%', padding: '5px', border: '1px solid var(--input-border)', borderRadius: '4px', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}>
                                <option value="" disabled>-- Select a Truck --</option>
                                {vehicles.filter(v => v.truck_number.toLowerCase().includes(vehicleSearch.toLowerCase())).map((v) => (
                                    <option key={v.id} value={v.id} style={{ padding: '5px' }}>Truck #{v.truck_number} ({v.make} {v.model})</option>
                                ))}
                            </select>
                        </div>
                        <button type="submit" disabled={loading || !vehicleId} className="btn btn-primary" style={{ width: '100%' }}>
                            {loading ? 'Assigning...' : 'Assign Vehicle'}
                        </button>
                    </form>
                )}

                {(space.status === 'RED' || space.status === 'YELLOW') && (
                    <div>
                        <div style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 10px 0' }}>Vehicle Details</h3>
                            {space.vehicle ? (
                                <>
                                    <p style={{ margin: '5px 0' }}><strong>Truck #:</strong> {space.vehicle.truck_number}</p>
                                    <p style={{ margin: '5px 0' }}><strong>Make/Model:</strong> {space.vehicle.year} {space.vehicle.make} {space.vehicle.model}</p>
                                    <p style={{ margin: '5px 0' }}><strong>Plate:</strong> {space.vehicle.plate_number}</p>
                                </>
                            ) : (
                                <p>No vehicle details available.</p>
                            )}
                        </div>
                        <button onClick={handleRelease} disabled={loading} className="btn btn-danger" style={{ width: '100%' }}>
                            {loading ? 'Releasing...' : 'Release Space'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpaceModal;